/**
 * Stored CampaignSequence steps -> SequencePreviewPanel step cards.
 *
 * Shape straight out of Mongo (verified against a real sequence, not the API
 * docs): steps is { "1": { variants: { "1": TouchPoint } } } keyed by POSITION,
 * and the TouchPoint carries `content` (not `body`), `notes` for the manual
 * channels, and channel names that do NOT match the panel's own vocabulary —
 * `phone`, `deepResearch` and `manualEmail` all used to fall through to the
 * email icon because STEP_META only knows call/deep_research/custom_task.
 */

// Stored touchpoint type -> the panel's channel vocabulary.
const TYPE_MAP = {
  email: "email",
  manualEmail: "email",
  linkedin: "linkedin",
  phone: "call",
  call: "call",
  deepResearch: "deep_research",
  task: "custom_task",
  customTask: "custom_task"
};

// LinkedIn task type -> the action the panel labels.
const LI_ACTION_MAP = {
  viewProfile: "view_profile",
  connectionRequest: "connection_request",
  message: "message",
  inMail: "inmail",
  interactWithPost: "interact_with_post"
};

const text = value => (typeof value === "string" ? value : "");

export const sequenceStepsToPreview = steps => {
  if (!steps || typeof steps !== "object") return [];

  return Object.entries(steps)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([position, step]) => {
      // Pull the field first then default: Java sends null, and a nested
      // destructure default only fires for undefined.
      const variants = (step && step.variants) || {};
      const keys = Object.keys(variants);
      const first = variants[keys[0]] || {};
      const rawType = text(first.type) || text(step && step.type);

      return {
        name: `Step ${position}`,
        type: TYPE_MAP[rawType] || "custom_task",
        days_later: first.daysLater || 0,
        subject: text(first.subject) || undefined,
        // `content` is the stored body for every email-ish touchpoint.
        body: text(first.content) || undefined,
        // Manual channels (LinkedIn / call / task) carry their instruction here.
        message: text(first.notes) || text(first.objectiveText) || undefined,
        linkedin_action: LI_ACTION_MAP[text(first.linkedInTaskType)],
        // manualEmail is a task for a human, not an automated send — the panel
        // marks it so a rep can tell what will go out on its own.
        automated: rawType !== "manualEmail",
        // Empty subject/body on an aiGenerated step is not missing data: the
        // copy is written per contact at send time.
        aiGenerated: first.aiGenerated === true,
        variantCount: keys.length > 1 ? keys.length : undefined
      };
    });
};

export default sequenceStepsToPreview;
